"use client";

import { GripVertical, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";

import {
  chatDisplayTitleForSession,
  subscribeChatHistory,
} from "@/features/chat/multi/history/chat-history";
import { cn } from "@/shared/lib/utils/cn";
import {
  pointerForTraceEvent,
  traceLineTone,
  useDevTraceStore,
  type DevTracePointer,
  type DevTraceRow,
  type TraceLineTone,
} from "./dev-trace-store";
import { isSessionTracesEnabled } from "./session-traces-enabled";
import { flowopsFetch } from "@/shared/builder-session/flowops-api-url";

const POINTER_LABEL: Record<DevTracePointer, string> = {
  chats: "Chats",
  steps: "Steps",
  transcript: "Transcript",
  intent: "Intent",
  plan: "Plan",
  hitl: "HITL",
  checklist: "Checklist",
  composer: "Composer",
  error: "Error",
};

type ListedTrace = {
  traceId: string;
  label?: string;
  prompt?: string;
  buildSessionId?: string;
  startedAt?: string;
  role?: string;
};

type ChatOption = {
  id: string;
  title: string;
  startedAt?: string;
};

function sessionIdFromUrl() {
  if (typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("sessionId")?.trim() ?? "";
}

function shortId(value?: string) {
  if (!value) return "—";
  return value.slice(0, 8);
}

function runLabel(item: ListedTrace) {
  const role = item.label || item.role || "run";
  const detail = item.prompt?.trim() || item.traceId.slice(0, 8);
  return `${role} · ${detail.slice(0, 36)}`;
}

function chatTitleFor(chatId: string, traces: ListedTrace[]) {
  const fromHistory = chatDisplayTitleForSession(chatId);
  if (fromHistory && !/^(new chat|new draft)$/i.test(fromHistory)) return fromHistory;
  const match = traces.find((item) => item.buildSessionId === chatId && item.prompt?.trim());
  return match?.prompt?.trim().slice(0, 40) || `Chat ${shortId(chatId)}`;
}

function clock(ts: string) {
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts.slice(11, 19) || ts;
  return date.toLocaleTimeString([], { hour12: false });
}

function payloadPreview(data: unknown): string {
  if (data == null) return "";
  if (typeof data === "string") return data.slice(0, 160);
  if (typeof data !== "object") return String(data);
  const record = data as Record<string, unknown>;
  const tool = typeof record.tool === "string" ? record.tool : "";
  const summary = typeof record.summary === "string" ? record.summary : "";
  const message = typeof record.message === "string" ? record.message : "";
  const kind = typeof record.kind === "string" ? record.kind : "";
  const parts = [tool, kind, summary || message].filter(Boolean);
  if (parts.length) return parts.join("  ");
  try {
    return JSON.stringify(data).slice(0, 160);
  } catch {
    return "";
  }
}

function toneTag(tone: TraceLineTone) {
  if (tone === "err") return "ERR";
  if (tone === "ok") return "OK ";
  return "   ";
}

function TracePointers({ focus }: { focus: DevTracePointer | null }) {
  const [boxes, setBoxes] = useState<Array<{ id: DevTracePointer; top: number; left: number }>>([]);

  useEffect(() => {
    const update = () => {
      const next: Array<{ id: DevTracePointer; top: number; left: number }> = [];
      for (const node of document.querySelectorAll<HTMLElement>("[data-trace]")) {
        const id = node.dataset.trace as DevTracePointer | undefined;
        if (!id || !(id in POINTER_LABEL)) continue;
        const rect = node.getBoundingClientRect();
        if (rect.width < 8 || rect.height < 8) continue;
        next.push({ id, top: Math.max(8, rect.top + 6), left: Math.max(8, rect.left + 6) });
      }
      setBoxes(next);
    };
    update();
    const timer = window.setInterval(update, 400);
    window.addEventListener("resize", update);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("resize", update);
    };
  }, []);

  useEffect(() => {
    if (!focus) return;
    const node = document.querySelector<HTMLElement>(`[data-trace="${focus}"]`);
    node?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focus]);

  if (!boxes.length) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[70]">
      {boxes.map((box) => (
        <span
          key={`${box.id}-${box.top}-${box.left}`}
          className={cn(
            "absolute rounded-md px-1.5 py-0.5 text-[10px] font-semibold shadow-sm",
            focus === box.id
              ? "bg-primary text-primary-foreground"
              : "bg-foreground/80 text-background",
          )}
          style={{ top: box.top, left: box.left }}
        >
          {POINTER_LABEL[box.id]}
        </span>
      ))}
    </div>
  );
}

export function DevTraceDock() {
  const pathname = usePathname();
  const { open, broken, rows, focusPointer, setOpen, setFocusPointer, replaceDisk } =
    useDevTraceStore();
  const [diskError, setDiskError] = useState<string | null>(null);
  const [traces, setTraces] = useState<ListedTrace[]>([]);
  const [selectedChatId, setSelectedChatId] = useState("");
  const [selectedTraceId, setSelectedTraceId] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [chatHistoryTick, setChatHistoryTick] = useState(0);
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  const lines = useMemo(() => {
    const merged = rows.filter((row) =>
      errorsOnly ? row.broken || traceLineTone(row.event, row.data) === "err" : true,
    );
    return merged.slice(-240);
  }, [errorsOnly, rows]);

  const chats = useMemo(() => {
    const map = new Map<string, ChatOption>();
    for (const item of traces) {
      const id = item.buildSessionId?.trim();
      if (!id || map.has(id)) continue;
      map.set(id, {
        id,
        title: chatTitleFor(id, traces),
        startedAt: item.startedAt,
      });
    }
    void chatHistoryTick;
    return [...map.values()];
  }, [chatHistoryTick, traces]);

  const runsForChat = useMemo(
    () => traces.filter((item) => item.buildSessionId === selectedChatId),
    [selectedChatId, traces],
  );
  const activeTraceId =
    selectedTraceId && runsForChat.some((item) => item.traceId === selectedTraceId)
      ? selectedTraceId
      : (runsForChat[0]?.traceId ?? "");

  const defaultPos = useMemo(() => {
    if (!mounted) return { x: 16, y: 72 };
    const width = Math.min(window.innerWidth - 32, 560);
    return {
      x: Math.max(16, window.innerWidth - width - 16),
      y: 72,
    };
  }, [mounted]);
  const panelPos = pos ?? defaultPos;

  useEffect(() => subscribeChatHistory(() => setChatHistoryTick((tick) => tick + 1)), []);

  useEffect(() => {
    if (!open) return;
    const fromUrl = sessionIdFromUrl();
    let cancelled = false;
    void (async () => {
      setDiskError(null);
      try {
        const urls = fromUrl
          ? [
              `/api/workflow/traces?sessionId=${encodeURIComponent(fromUrl)}`,
              "/api/workflow/traces?limit=60",
            ]
          : ["/api/workflow/traces?limit=60"];
        const lists = await Promise.all(
          urls.map(async (url) => {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Could not load traces");
            const body = (await response.json()) as { traces?: ListedTrace[] };
            return body.traces ?? [];
          }),
        );
        if (cancelled) return;
        const byId = new Map<string, ListedTrace>();
        for (const list of lists) {
          for (const item of list) {
            if (item.traceId) byId.set(item.traceId, item);
          }
        }
        const list = [...byId.values()].sort((a, b) =>
          String(b.startedAt ?? "").localeCompare(String(a.startedAt ?? "")),
        );
        setTraces(list);

        const chatIds = [
          ...new Set(list.map((item) => item.buildSessionId?.trim()).filter(Boolean) as string[]),
        ];
        const preferredChat =
          (fromUrl && chatIds.includes(fromUrl) ? fromUrl : "") || chatIds[0] || "";
        setSelectedChatId((current) =>
          current && chatIds.includes(current) ? current : preferredChat,
        );
      } catch (error) {
        if (!cancelled) setDiskError(error instanceof Error ? error.message : "Trace fetch failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, pathname]);

  useEffect(() => {
    if (!open || !activeTraceId) return;
    let cancelled = false;
    void flowopsFetch(`/api/workflow/traces/${encodeURIComponent(activeTraceId)}`)
      .then(async (response) => {
        if (!response.ok) throw new Error("Could not read trace file");
        const payload = (await response.json()) as {
          events?: Array<{ ts: string; type: string; payload: unknown; traceId: string }>;
        };
        if (cancelled) return;
        const sessionId =
          traces.find((item) => item.traceId === activeTraceId)?.buildSessionId ||
          selectedChatId ||
          sessionIdFromUrl();
        replaceDisk(
          (payload.events ?? []).map((event, index) => ({
            id: `disk-${event.traceId}-${index}`,
            ts: event.ts,
            chatId: sessionId,
            event: event.type,
            source: "disk" as const,
            data: event.payload,
            broken: event.type === "error",
            pointer: pointerForTraceEvent(event.type, event.payload),
          })),
        );
      })
      .catch((error) => {
        if (!cancelled) setDiskError(error instanceof Error ? error.message : "Trace fetch failed");
      });
    return () => {
      cancelled = true;
    };
  }, [activeTraceId, open, replaceDisk, selectedChatId, traces]);

  useEffect(() => {
    if (!open) return;
    const node = logRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [lines.length, open]);

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!drag.current) return;
      const panel = panelRef.current;
      const w = panel?.offsetWidth ?? 560;
      const h = panel?.offsetHeight ?? 560;
      setPos({
        x: Math.min(window.innerWidth - w - 8, Math.max(8, event.clientX - drag.current.dx)),
        y: Math.min(window.innerHeight - h - 8, Math.max(8, event.clientY - drag.current.dy)),
      });
    };
    const onUp = () => {
      drag.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  if (!isSessionTracesEnabled() || !mounted || !open) return null;

  const selected = runsForChat.find((item) => item.traceId === activeTraceId);
  const selectedChat = chats.find((chat) => chat.id === selectedChatId);

  return createPortal(
    <>
      <TracePointers focus={focusPointer} />

      <div
        ref={panelRef}
        className="fixed z-[2147483646] flex h-[min(78vh,560px)] w-[min(100vw-2rem,560px)] flex-col overflow-hidden rounded-xl border border-zinc-700 bg-zinc-950 text-zinc-100 shadow-xl"
        style={{ left: panelPos.x, top: panelPos.y }}
      >
        <div
          className="flex cursor-grab touch-none select-none items-center justify-between gap-2 border-b border-zinc-800 px-2 py-2 active:cursor-grabbing"
          onPointerDown={(event) => {
            if ((event.target as HTMLElement).closest("button")) return;
            const panel = panelRef.current;
            if (!panel) return;
            const rect = panel.getBoundingClientRect();
            drag.current = { dx: event.clientX - rect.left, dy: event.clientY - rect.top };
          }}
        >
          <GripVertical className="size-3.5 shrink-0 text-zinc-500" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[12px] font-semibold">chat traces</p>
            <p className={cn("font-mono text-[10px]", broken ? "text-red-400" : "text-zinc-500")}>
              {broken ? "ERR  agent stopped or a step failed" : "OK   pick a chat, then a run"}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close traces"
            onClick={() => setOpen(false)}
            className="rounded-md p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
          >
            <X className="size-3.5" />
          </button>
        </div>

        <div className="space-y-2 border-b border-zinc-800 px-3 py-2">
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] text-zinc-500">Chat</span>
            <select
              value={selectedChatId}
              onChange={(event) => setSelectedChatId(event.target.value)}
              className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 font-mono text-[11px] text-zinc-100"
            >
              {chats.length === 0 ? <option value="">No chats with traces yet</option> : null}
              {chats.map((chat) => (
                <option key={chat.id} value={chat.id}>
                  {chat.title} · {shortId(chat.id)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end gap-2">
            <label className="min-w-0 flex-1">
              <span className="mb-1 block font-mono text-[10px] text-zinc-500">Run</span>
              <select
                value={activeTraceId}
                onChange={(event) => setSelectedTraceId(event.target.value)}
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 font-mono text-[11px] text-zinc-100"
              >
                {runsForChat.length === 0 ? <option value="">No runs for this chat</option> : null}
                {runsForChat.map((item) => (
                  <option key={item.traceId} value={item.traceId}>
                    {runLabel(item)}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => setErrorsOnly((value) => !value)}
              className={cn(
                "shrink-0 rounded-md border px-2 py-1 font-mono text-[10px]",
                errorsOnly
                  ? "border-red-500/40 bg-red-500/15 text-red-300"
                  : "border-zinc-700 text-zinc-400",
              )}
            >
              {errorsOnly ? "err only" : "all"}
            </button>
          </div>
        </div>

        {selected || selectedChat ? (
          <p className="truncate border-b border-zinc-800 px-3 py-1 font-mono text-[10px] text-zinc-500">
            chat {shortId(selectedChatId)}
            {selected
              ? `  run ${selected.label || selected.role || shortId(selected.traceId)}`
              : ""}
          </p>
        ) : null}

        <div
          ref={logRef}
          className="min-h-0 flex-1 cursor-text select-text overflow-y-auto px-2 py-2 font-mono text-[11px] leading-5"
        >
          {diskError ? <p className="px-1 pb-2 text-red-400">{diskError}</p> : null}
          {!lines.length ? (
            <p className="px-1 py-6 text-center text-zinc-500">
              no log lines yet — pick a chat and run above
            </p>
          ) : null}
          {lines.map((row) => (
            <TraceLine
              key={row.id}
              row={row}
              expanded={expandedId === row.id}
              onToggle={() => {
                setExpandedId((current) => (current === row.id ? null : row.id));
                setFocusPointer(row.pointer);
              }}
            />
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
}

function TraceLine({
  row,
  expanded,
  onToggle,
}: {
  row: DevTraceRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const tone = traceLineTone(row.event, row.data);
  const preview = payloadPreview(row.data);
  return (
    <div
      className="w-full rounded-sm px-1 py-0.5 text-left select-text hover:bg-zinc-900"
      onClick={() => {
        const selected = window.getSelection()?.toString();
        if (selected) return;
        onToggle();
      }}
    >
      <span
        className={cn(
          "mr-2 inline-block w-8",
          tone === "err" ? "text-red-400" : tone === "ok" ? "text-emerald-400" : "text-zinc-500",
        )}
      >
        {toneTag(tone)}
      </span>
      <span className="mr-2 text-zinc-500">{clock(row.ts)}</span>
      <span className={tone === "err" ? "text-red-300" : "text-zinc-200"}>{row.event}</span>
      {preview ? <span className="ml-2 text-zinc-500">{preview}</span> : null}
      {expanded ? (
        <pre className="mt-1 mb-2 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-zinc-900 p-2 text-[10px] text-zinc-400 select-text">
          {JSON.stringify(row.data, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}
